import { capitalize, kebabCase } from 'lodash';
import { hasValue, isValidValue } from '.';
import {
  ControlField,
  FieldMetadata,
} from '../../../../contexts/MetadataContext';
import { PropertyField } from '../../../../system/field';
import {
  Field,
  FieldsAndSourceList,
  FieldsList,
  ListingAction,
  ListingActionType,
  ListingMetrics,
  ListingMetricType,
  ListingRecord,
  ListingSection,
  ListingState,
  ListingStateKey,
  PayloadTypes,
  RecordTypes,
  RecordValue,
  UnselectedRecordList,
} from './types';
import {
  filterResolvedData,
  getFieldsDifference,
  hasResolvedData,
  isExcludedSourceField,
  isHiddenField,
  isReadyToImportField,
  isSourceField,
  shouldReviewField,
} from './reducerUtils';
import { checkIfNeedsReview } from './reviewUtils';
import {
  countRecordData,
  getStateValueFromRecord,
  isRecordEnabled,
} from './recordUtils';

export const getReadyToImportValue = (field: Field): RecordValue | null => {
  if (!field.readyToImportValues?.hasValues) {
    return null;
  }

  const finalValue: RecordValue | null = Object.entries(
    field.readyToImportValues.added || {},
  ).reduce((result, [key, recordValue]: any) => {
    const valueIsArray = Array.isArray(recordValue.value);
    const resultIsArray = Array.isArray(result);
    const newValue =
      valueIsArray && resultIsArray
        ? [...result, ...recordValue.value]
        : recordValue.value;

    return {
      ...recordValue,
      value: newValue,
    };
  }, null);

  return finalValue;
};

const setHiddenFields = (
  state: ListingState,
  payload: PayloadTypes.HiddenFieldsPayload,
): ListingState => {
  const { type, fields } = payload;

  return {
    ...state,
    [ListingStateKey.HIDDEN_FIELDS]: {
      ...state[ListingStateKey.HIDDEN_FIELDS],
      [type]: [...fields],
    },
  };
};

export const getUnselectedFields = (
  state: ListingState,
): UnselectedRecordList => {
  let unselectedRecords: UnselectedRecordList = {};
  const includeUnselectedListings =
    state[ListingStateKey.CONFIG].includeUnselectedListings;
  const records = state[ListingStateKey.RECORDS];
  const recordsFieldList = state[ListingStateKey.RECORDS_FIELDLIST];
  const readyToImportList = state[ListingStateKey.READY_TO_IMPORT_LIST];
  let filteredRecords: ListingRecord[] = [...records];

  if (!includeUnselectedListings) {
    const enabledRecords = state[ListingStateKey.ENABLED_RECORDS];
    filteredRecords = records.reduce(
      (recordsList: ListingRecord[], record: ListingRecord) => {
        if (enabledRecords[record.id].isEnabled) {
          return [...recordsList, { ...record }];
        }
        return recordsList;
      },
      [],
    );
  }

  filteredRecords.forEach((record: ListingRecord) => {
    const diff = getFieldsDifference(record.Data, readyToImportList.fields);
    const unselectedFields = Object.keys(diff).reduce(
      (fields: any, key: string) => {
        const field = recordsFieldList.fields[key];
        if (field && hasValue(diff[key])) {
          fields = {
            ...fields,
            [key]: field,
          };
        }
        return fields;
      },
      {},
    );
    const template = state[ListingStateKey.TEMPLATE];
    if (!template.current) return {};

    const unselectedFieldsBySection = processFieldsFromTemplate(
      template[template.current],
      unselectedFields,
      { sections: true },
      state[ListingStateKey.CONFIG].getFieldMetadata,
    );

    unselectedRecords = {
      ...unselectedRecords,
      [record.id]: {
        recordId: record.id,
        date: record.Data.MLS_LIST_DATE,
        unselectedFieldsData: diff,
        listedSections: unselectedFieldsBySection,
      },
    };
  });

  return unselectedRecords;
};

const getMetrics = (state: ListingState): ListingMetrics => {
  const initialMetrics: ListingMetrics = Object.keys(ListingMetricType).reduce(
    (metrics, k) => ({ ...metrics, [k]: 0 }),
    {},
  );

  const templateFieldList: FieldsAndSourceList =
    state[ListingStateKey.TEMPLATE_FIELDLIST];
  const readyToImportFieldList: FieldsAndSourceList =
    state[ListingStateKey.READY_TO_IMPORT_LIST];

  initialMetrics[ListingMetricType.READY_TO_IMPORT] = (
    readyToImportFieldList.sections || []
  ).reduce((total, section) => total + section.totalFields, 0);

  return Object.values(templateFieldList.fields || {}).reduce(
    (totals: any, field: Field) => {
      if (isHiddenField(field, state)) return totals;

      if (shouldReviewField(field)) {
        totals[ListingMetricType.NEEDS_REVIEW] += 1;
      } else {
        totals[ListingMetricType.NO_CONFLICT_FIELDS] += 1;
      }

      if (!hasResolvedData(field, state)) {
        if (shouldReviewField(field)) {
          totals[ListingMetricType.REMAINING_REVIEW_FIELDS] += 1;
        }
      } else {
        totals[ListingMetricType.SOLVED_BY_USER] += 1;
      }

      return totals;
    },
    initialMetrics,
  );
};

const addFieldToFieldList = (
  fieldToAdd: Field,
  fieldMetadata: FieldMetadata | null,
  fieldList: FieldsList,
): FieldsList => {
  const key = fieldMetadata?.Key || fieldToAdd?.key;
  let field: Field = fieldList[key];
  if (!field) {
    field = {
      ...fieldToAdd,
      metadata: fieldMetadata,
      readyToImportValues: {
        added: {
          [fieldToAdd.currentRecordData.recordId]: {
            ...fieldToAdd.currentRecordData,
          },
        },
        hasValues: true,
      },
    };
  }

  const recordId = fieldToAdd.currentRecordData?.recordId;
  if (recordId && field?.recordData) {
    field.recordData[recordId] = fieldToAdd.currentRecordData.value;
  }

  const needsReview = field
    ? checkIfNeedsReview(field, fieldToAdd.currentRecordData)
    : null;

  if (needsReview?.hasConflictData) {
    // If we have conflict data, readyToImportValues are removed
    const { readyToImportValues: _, ...restOfField } = field;
    field = {
      ...restOfField,
    };
  }

  return {
    ...fieldList,
    [key]: {
      ...field,
      reviewProps: needsReview,
    },
  } as FieldsList;
};

const getReadyToImportFields = (state: ListingState): FieldsAndSourceList => {
  const template = state[ListingStateKey.TEMPLATE];
  const templateFieldList = state[ListingStateKey.TEMPLATE_FIELDLIST];
  const fields: Field[] = Object.values(templateFieldList.fields || {});

  if (!fields?.length) {
    return {
      fields: {},
      sourceFields: {},
    };
  }

  const fieldsToRender: FieldsList = fields.reduce(
    (fieldsList: FieldsList, field: Field) => {
      const { key } = field;

      if (isReadyToImportField(field)) {
        fieldsList = {
          ...fieldsList,
          [key]: field,
        };
      }
      return fieldsList;
    },
    {},
  );

  return processFieldsFromTemplate(
    template[template.current],
    fieldsToRender,
    { sections: true },
    state[ListingStateKey.CONFIG].getFieldMetadata,
  );
};

export const processFieldsFromTemplate = (
  templateData: any,
  fieldListToCheck: FieldsList,
  opts?: any,
  getFieldMetadata?: Function,
): FieldsAndSourceList => {
  const fieldsList: FieldsAndSourceList = Object.values(templateData).reduce(
    (
      fieldsByTemplate: FieldsAndSourceList,
      group: any,
    ): FieldsAndSourceList => {
      const filteredFields = group.flattenFields.reduce(
        (filtered: any, controlField: ControlField) => {
          const key = controlField.Key;
          const listedField = filtered.listedFields[key];
          const inTemplate = !listedField && key && fieldListToCheck?.[key];
          let fieldSourceKey = '';
          if (key && getFieldMetadata) {
            const fieldSourceMetadata = controlField.SourceKey
              ? getFieldMetadata(controlField.SourceKey)
              : getFieldMetadata(`${key}_SOURCE`);

            if (
              fieldSourceMetadata?.Key &&
              !isExcludedSourceField(fieldSourceMetadata?.Key)
            ) {
              fieldSourceKey = fieldSourceMetadata.Key;
              fieldsByTemplate.sourceFields[key] = fieldSourceMetadata.Key;
            }
          }

          if (inTemplate) {
            const field: Field = {
              ...fieldListToCheck[key],
              controlField,
              ...(fieldSourceKey
                ? {
                    sourceField: {
                      key: fieldSourceKey,
                      value: '',
                    },
                  }
                : {}),
            };
            filtered.fields = [...filtered.fields, { ...field }];
            filtered.listedFields[key] = true;
            fieldsByTemplate.fields[key] = { ...field };
          }

          return filtered;
        },
        { fields: [], listedFields: {} } as any,
      );

      if (!filteredFields.fields.length) return fieldsByTemplate;
      if (opts.sections) {
        fieldsByTemplate.sections = [
          ...(fieldsByTemplate.sections || []),
          {
            ...filteredFields,
            title: group.title,
            key: kebabCase(group.title),
            totalFields: filteredFields.fields.length,
          },
        ] as ListingSection[];
      }
      return fieldsByTemplate;
    },
    { sections: [], fields: {}, sourceFields: {} } as FieldsAndSourceList,
  );
  return fieldsList;
};

export const processFieldsFromRecommendations = (
  recommendations: any[],
  recommendationFilter?: Function | null,
  fieldFilter?: Function | null,
  getFieldMetadata?: Function | null,
) => {
  return recommendations.reduce(
    (
      localFieldsList: FieldsAndSourceList,
      recommendation: any,
    ): FieldsAndSourceList => {
      if (recommendationFilter && !recommendationFilter(recommendation))
        return localFieldsList;

      const key = recommendation.featurename;
      const fieldToAdd: Field = {
        key,
        currentRecordData: {
          value: recommendation.predictedvalue,
          label: '',
          recordId: '',
          recordType: '',
        },
      };
      if (fieldFilter && fieldFilter(fieldToAdd)) return localFieldsList;
      const metadata =
        key && getFieldMetadata
          ? (getFieldMetadata(key) as FieldMetadata)
          : null;
      const listWithField: FieldsList = addFieldToFieldList(
        fieldToAdd,
        metadata,
        localFieldsList.fields,
      );

      return {
        fields: listWithField,
        sourceFields: localFieldsList.sourceFields,
      };
    },
    { fields: {}, sourceFields: {} },
  );
};

// Auto-resolve fields with single valid option after filtering
const autoResolveSingleOptionFields = (
  fieldsList: FieldsAndSourceList,
  state: ListingState,
): FieldsAndSourceList => {
  const updatedFields = { ...fieldsList.fields };

  Object.entries(updatedFields).forEach(([key, field]) => {
    // Check if field needs review and has only one option
    if (
      field.reviewProps?.hasConflictData &&
      field.reviewProps.optionsToShow.length === 1 &&
      !field.resolvedData // Not already resolved by user
    ) {
      const singleOption = field.reviewProps.optionsToShow[0];

      // Auto-resolve to the single valid option
      updatedFields[key] = {
        ...field,
        readyToImportValues: {
          added: {
            [singleOption.recordId]: {
              ...singleOption,
            },
          },
          hasValues: true,
        },
        reviewProps: {
          ...field.reviewProps,
          hasConflictData: false, // No longer a conflict
          autoResolved: true, // Mark as auto-resolved for debugging
        },
      };
    }
  });

  return {
    ...fieldsList,
    fields: updatedFields,
  };
};

export const processFieldsFromRecords = (
  records: ListingRecord[],
  recordFilter?: Function | null,
  fieldFilter?: Function | null,
  getFieldMetadata?: Function | null,
) => {
  // TODO: fieldsData should be a Type
  return records.reduce(
    (fieldsData: any, record: ListingRecord) => {
      if (recordFilter && !recordFilter(record)) return fieldsData;
      return (
        Object.entries(record.Data) as Array<[PropertyField, any]>
      ).reduce(
        (
          localFieldsData: FieldsAndSourceList,
          [key, fieldValue],
        ): FieldsAndSourceList => {
          if (!isValidValue(fieldValue)) return localFieldsData;
          const listingType = capitalize(record.Type);
          const fieldToBeAdded: Field = {
            key,
            currentRecordData: {
              value: fieldValue,
              label:
                record.type === 'AI'
                  ? 'AI recommendation'
                  : `${record.Year} ${listingType}`,
              recordId: record.id,
              recordType: record.Type,
            },
            recordData: {},
          };

          if (fieldFilter && fieldFilter(fieldToBeAdded)) {
            return localFieldsData;
          }

          if (isSourceField(key) && fieldValue) {
            localFieldsData.sourceFields[key] = {
              ...(localFieldsData.sourceFields[key] || {}),
              [record.id]: fieldValue,
            };
          }

          const metadata =
            key && getFieldMetadata
              ? (getFieldMetadata(key) as FieldMetadata)
              : null;
          const listWithField: FieldsList = addFieldToFieldList(
            fieldToBeAdded,
            metadata,
            localFieldsData.fields,
          );

          return {
            fields: listWithField,
            sourceFields: localFieldsData.sourceFields,
          };
        },
        fieldsData,
      );
    },
    { fields: {}, sourceFields: {} },
  );
};

const processFields = (state: ListingState): ListingState => {
  // Get ready to import fields
  let readyToImportList = getReadyToImportFields(state);

  // Auto-resolve single option fields
  readyToImportList = autoResolveSingleOptionFields(readyToImportList, state);

  // Also update template field list with auto-resolved fields
  const templateFieldList = state[ListingStateKey.TEMPLATE_FIELDLIST];
  const autoResolvedTemplateFields = autoResolveSingleOptionFields(
    templateFieldList,
    state,
  );

  const newState: ListingState = {
    ...state,
    [ListingStateKey.TEMPLATE_FIELDLIST]: autoResolvedTemplateFields,
    [ListingStateKey.READY_TO_IMPORT_LIST]: readyToImportList,
  };

  return {
    ...newState,
    [ListingStateKey.METRICS]: {
      ...newState[ListingStateKey.METRICS],
      ...getMetrics(newState),
    },
  };
};

const enableRecord = (
  state: ListingState,
  payload: PayloadTypes.EnableRecordPayload,
): ListingState => {
  const { record } = payload;
  const { [ListingStateKey.ENABLED_RECORDS]: enabledRecords = {} } = state;
  const enabledRecord = enabledRecords[record.id];

  if (!enabledRecord) return state;
  let newState: ListingState = {
    ...state,
    [ListingStateKey.ENABLED_RECORDS]: {
      ...enabledRecords,
      [record.id]: {
        ...enabledRecord,
        isEnabled: !enabledRecord?.isEnabled,
      },
    },
  };

  const fieldListFromEnabledRecords = processFieldsFromRecords(
    newState[ListingStateKey.RECORDS],
    (record: ListingRecord) => isRecordEnabled(record.id, newState),
    (field: Field) => isHiddenField(field, state),
    state[ListingStateKey.CONFIG].getFieldMetadata,
  );

  const template = newState[ListingStateKey.TEMPLATE] || {};
  const templateData = template[template.current];
  const previousTemplateList = Object.keys(
    state[ListingStateKey.TEMPLATE_FIELDLIST].fields || {},
  ).reduce((acc, fieldKey) => ({ ...acc, [fieldKey]: true }), {});

  const templateFieldList = processFieldsFromTemplate(
    templateData,
    fieldListFromEnabledRecords.fields,
    { sections: true },
    newState[ListingStateKey.CONFIG].getFieldMetadata,
  );

  newState = {
    ...newState,
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      ...templateFieldList,
    },
    // This list of fields is added to know which of them should be
    // deleted from the context once a record is toggled off
    [ListingStateKey.PREVIOUS_TEMPLATE_LIST]: {
      ...previousTemplateList,
    },
    [ListingStateKey.RESOLVED_FIELDS]: {
      ...filterResolvedData(newState),
    },
  };

  return {
    ...processFields(newState),
  };
};

const setRecords = (
  state: ListingState,
  payload: PayloadTypes.SetRecordsPayload,
): ListingState => {
  const { records } = payload;

  if (!records?.length) {
    // Reset state
    return {
      ...state,
      [ListingStateKey.RECORDS]: [],
      [ListingStateKey.ENABLED_RECORDS]: {},
      [ListingStateKey.RECORDS_FIELDLIST]: {
        fields: {},
        sourceFields: {},
      },
      [ListingStateKey.READY_TO_IMPORT_LIST]: {
        fields: {},
        sourceFields: {},
      },
      [ListingStateKey.TEMPLATE_FIELDLIST]: {
        fields: {},
        sourceFields: {},
      },
    };
  }

  const enabledRecords = records.reduce(
    (recordsAcc: any, record: ListingRecord) => {
      const subText =
        (record.Type === RecordTypes.LISTING &&
          `${record.Data['PROPERTY_TYPE']} Listing`) ||
        (record.Type === RecordTypes.TAX && 'Tax Record') ||
        '';
      const label =
        record.Type === RecordTypes.AI
          ? 'AI Recommendations'
          : `${record.Year} ${subText}`;
      return {
        ...recordsAcc,
        [record.id]: {
          id: record.id,
          type: record.Type,
          label,
        },
      };
    },
    {},
  );

  const recordsFieldList = processFieldsFromRecords(
    records,
    null,
    (field: Field) => isHiddenField(field, state),
    state[ListingStateKey.CONFIG].getFieldMetadata,
  );

  const newState: ListingState = {
    ...state,
    [ListingStateKey.RECORDS]: records,
    [ListingStateKey.ENABLED_RECORDS]: enabledRecords,
    [ListingStateKey.RECORDS_FIELDLIST]: {
      ...recordsFieldList,
    },
    [ListingStateKey.READY_TO_IMPORT_LIST]: {
      fields: {},
      sourceFields: {},
    },
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      fields: {},
      sourceFields: {},
    },
  };

  return {
    ...updateTemplateFields(newState),
  };
};

const setTemplate = (
  state: ListingState,
  payload: PayloadTypes.SetTemplatePayload,
): ListingState => {
  const { templateName, templateData } = payload;

  let newState = {
    ...state,
    [ListingStateKey.TEMPLATE]: {
      ...state[ListingStateKey.TEMPLATE],
      [templateName]: templateData,
      current: templateName,
    },
  };

  const fieldListFromEnabledRecords = processFieldsFromRecords(
    state[ListingStateKey.RECORDS],
    (record: ListingRecord) => isRecordEnabled(record.id, newState),
    (field: Field) => isHiddenField(field, state),
    state[ListingStateKey.CONFIG].getFieldMetadata,
  );

  const templateFieldList = processFieldsFromTemplate(
    templateData,
    fieldListFromEnabledRecords.fields,
    { sections: true },
    state[ListingStateKey.CONFIG].getFieldMetadata,
  );

  newState = {
    ...newState,
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      ...templateFieldList,
    },
    [ListingStateKey.RESOLVED_FIELDS]: {},
  };

  return {
    ...processFields(newState),
  };
};

const updateTemplateFields = (state: ListingState): ListingState => {
  const template = state[ListingStateKey.TEMPLATE];
  const templateData = template[template.current];

  if (!templateData) {
    return state;
  }

  const fields = state[ListingStateKey.RECORDS_FIELDLIST].fields;
  const fieldList = processFieldsFromTemplate(templateData, fields, {
    sections: true,
  });

  const recordDataMetrics = countRecordData(fieldList.fields);
  return {
    ...state,
    [ListingStateKey.METRICS]: {
      ...state[ListingStateKey.METRICS],
      [state[ListingStateKey.TEMPLATE].current]: {
        ...recordDataMetrics,
      },
      ...getMetrics(state),
    },
  };
};

const addToReadyToImportValues = (
  state: ListingState,
  field: Field,
  newValue: RecordValue,
): ListingState => {
  const { recordId, value } = newValue;
  const record = state[ListingStateKey.ENABLED_RECORDS][recordId];
  const fieldFromTemplate: Field =
    state[ListingStateKey.TEMPLATE_FIELDLIST].fields[field.key];
  const readyToImportValues = fieldFromTemplate?.readyToImportValues || {};
  const newReadyToImportValues = {
    ...readyToImportValues,
    hasValues: true,
    added: {
      [recordId]: {
        label: record?.label,
        recordId,
        recordType: record?.type,
        value,
      },
    },
  };

  const fieldFromRecordList: Field =
    state[ListingStateKey.RECORDS_FIELDLIST].fields[field.key];
  const fieldFromTemplateList: Field =
    state[ListingStateKey.TEMPLATE_FIELDLIST].fields[field.key];
  const newState: ListingState = {
    ...state,
    [ListingStateKey.RECORDS_FIELDLIST]: {
      ...state[ListingStateKey.RECORDS_FIELDLIST],
      fields: {
        ...state[ListingStateKey.RECORDS_FIELDLIST].fields,
        [field.key]: {
          ...fieldFromRecordList,
          readyToImportValues: { ...newReadyToImportValues },
        },
      },
    },
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      ...state[ListingStateKey.TEMPLATE_FIELDLIST],
      fields: {
        ...state[ListingStateKey.TEMPLATE_FIELDLIST].fields,
        [field.key]: {
          ...fieldFromRecordList,
          ...fieldFromTemplateList,
          readyToImportValues: { ...newReadyToImportValues },
        },
      },
    },
  };

  return {
    ...processFields(newState),
  };
};

const addField = (
  state: ListingState,
  payload: PayloadTypes.AddFieldPayload,
): ListingState => {
  const { field, recordValue } = payload;
  const { recordId, value } = recordValue;
  const record = state[ListingStateKey.ENABLED_RECORDS][recordId];

  if (!record || !hasValue(value)) {
    return { ...state };
  }

  return addToReadyToImportValues(state, field, recordValue);
};

const removeField = (
  state: ListingState,
  payload: PayloadTypes.RemoveFieldPayload,
): ListingState => {
  const { field, recordValue } = payload;
  const { recordId, value } = recordValue;

  if (!recordId || !hasValue(value)) {
    return {
      ...state,
    };
  }

  const fieldFromTemplate: Field =
    state[ListingStateKey.TEMPLATE_FIELDLIST].fields[field.key];
  const currentValues = fieldFromTemplate?.readyToImportValues || {};
  const { added: addedValues }: any = currentValues;
  const { [recordId]: valueFromRecord, ...restOfValues }: any = addedValues;
  const arrayValues =
    Array.isArray(valueFromRecord.value) &&
    valueFromRecord.value.includes(value)
      ? valueFromRecord.value.filter((v: any) => v !== value)
      : [];

  const hasValues =
    !!Object.keys(restOfValues || {}).length || !!arrayValues.length;
  const newCurrentValues = hasValues
    ? {
        hasValues,
        added: {
          ...(arrayValues.length
            ? { [recordId]: { ...valueFromRecord, value: arrayValues } }
            : {}),
          ...restOfValues,
        },
      }
    : { hasValues };

  const newState: ListingState = {
    ...state,
    [ListingStateKey.RECORDS_FIELDLIST]: {
      ...state[ListingStateKey.RECORDS_FIELDLIST],
      fields: {
        ...state[ListingStateKey.RECORDS_FIELDLIST].fields,
        [field.key]: {
          ...state[ListingStateKey.RECORDS_FIELDLIST].fields[field.key],
          readyToImportValues: { ...newCurrentValues },
        },
      },
    },
    [ListingStateKey.TEMPLATE_FIELDLIST]: {
      ...state[ListingStateKey.TEMPLATE_FIELDLIST],
      fields: {
        ...state[ListingStateKey.TEMPLATE_FIELDLIST].fields,
        [field.key]: {
          ...state[ListingStateKey.TEMPLATE_FIELDLIST].fields[field.key],
          readyToImportValues: { ...newCurrentValues },
        },
      },
    },
  };

  return {
    ...processFields(newState),
  };
};

const updateField = (
  state: ListingState,
  payload: PayloadTypes.UpdateFieldPayload,
): ListingState => {
  const { field, recordValue } = payload;
  const newRecordValue = getStateValueFromRecord(state, field.key, recordValue);

  return addToReadyToImportValues(state, field, newRecordValue);
};

const resolveField = (
  state: ListingState,
  payload: PayloadTypes.ResolveFieldPayload,
): ListingState => {
  const { field, value } = payload;
  const newState: ListingState = {
    ...state,
    [ListingStateKey.RESOLVED_FIELDS]: {
      ...state[ListingStateKey.RESOLVED_FIELDS],
      [field.key]: {
        ...value,
        persisted: false,
      },
    },
  };

  const shouldPersist = state[ListingStateKey.CONFIG].persistResolvedData;
  if (shouldPersist) {
    return {
      ...newState,
      ...persistResolved(newState, {}),
    };
  }

  const metrics = getMetrics(newState);
  return {
    ...newState,
    [ListingStateKey.METRICS]: {
      ...state[ListingStateKey.METRICS],
      ...metrics,
    },
  };
};

const persistResolved = (
  state: ListingState,
  payload: PayloadTypes.PersistResolvedPayload,
): ListingState => {
  let newState = {
    ...state,
  };
  const resolvedFields = state[ListingStateKey.RESOLVED_FIELDS];
  for (const [fieldKey, value] of Object.entries(resolvedFields || {})) {
    const templateField: Field =
      state[ListingStateKey.TEMPLATE_FIELDLIST].fields[fieldKey];
    const readyToImportField: Field =
      state[ListingStateKey.READY_TO_IMPORT_LIST].fields[fieldKey];
    const newValue = getStateValueFromRecord(state, fieldKey, value);

    const readyToImportValues = {
      toShow: newValue,
      added: {
        [value.recordId]: {
          ...newValue,
        },
      },
      hasValues: true,
    };

    newState = {
      ...newState,
      [ListingStateKey.RECORDS_FIELDLIST]: {
        ...newState[ListingStateKey.RECORDS_FIELDLIST],
        fields: {
          ...newState[ListingStateKey.RECORDS_FIELDLIST].fields,
          [fieldKey]: {
            ...newState[ListingStateKey.RECORDS_FIELDLIST].fields[fieldKey],
            readyToImportValues,
            resolvedData: value,
          },
        },
      },
      [ListingStateKey.TEMPLATE_FIELDLIST]: {
        ...newState[ListingStateKey.TEMPLATE_FIELDLIST],
        fields: {
          ...newState[ListingStateKey.TEMPLATE_FIELDLIST].fields,
          [fieldKey]: {
            ...templateField,
            readyToImportValues: {
              ...readyToImportValues,
            },
            resolvedData: value,
          },
        },
      },
      [ListingStateKey.READY_TO_IMPORT_LIST]: {
        ...newState[ListingStateKey.READY_TO_IMPORT_LIST],
        fields: {
          ...newState[ListingStateKey.READY_TO_IMPORT_LIST].fields,
          [fieldKey]: {
            ...readyToImportField,
            readyToImportValues: {
              ...readyToImportValues,
            },
            resolvedData: value,
          },
        },
      },
      [ListingStateKey.RESOLVED_FIELDS]: {
        ...newState[ListingStateKey.RESOLVED_FIELDS],
        [fieldKey]: {
          ...newState[ListingStateKey.RESOLVED_FIELDS][fieldKey],
          persisted: true,
        },
      },
    };
  }

  return {
    ...processFields(newState),
  };
};

const init = (
  state: ListingState,
  payload: PayloadTypes.InitPayload,
): ListingState => {
  const { getFieldMetadata } = payload;
  const newState: ListingState = {
    ...state,
    [ListingStateKey.CONFIG]: {
      ...state[ListingStateKey.CONFIG],
      getFieldMetadata,
    },
  };

  return newState;
};

export const listingAssistantReducer = (
  state: ListingState,
  action: ListingAction,
): ListingState => {
  const { type, payload } = action;

  switch (type) {
    case ListingActionType.INIT:
      return init(state, payload);
    case ListingActionType.ADD_FIELD:
      return addField(state, payload);
    case ListingActionType.REMOVE_FIELD:
      return removeField(state, payload);
    case ListingActionType.UPDATE_FIELD:
      return updateField(state, payload);
    case ListingActionType.RESOLVE_FIELD:
      return resolveField(state, payload);
    case ListingActionType.ENABLE_RECORD:
      return enableRecord(state, payload);
    case ListingActionType.SET_RECORDS:
      return setRecords(state, payload);
    case ListingActionType.SET_TEMPLATE:
      return setTemplate(state, payload);
    case ListingActionType.SET_HIDDEN_FIELDS:
      return setHiddenFields(state, payload);
    case ListingActionType.UPDATE_TEMPLATE_FIELDS:
      return updateTemplateFields(state);
    case ListingActionType.PERSIST_RESOLVED:
      return persistResolved(state, payload);
    default:
      return state;
  }
};
